/**
 * Re-Consent Checker for DSGVO Compliance
 *
 * This script checks if the current user needs to re-accept any mandatory
 * consents (DSGVO, AGB) due to version updates. If outdated consents are
 * found, it displays a modal banner prompting the user to accept.
 *
 * Usage:
 * Include this script in your page and it will automatically check
 * consent status on page load for logged-in users.
 */
(() => {
  "use strict";

  // Idempotency guard (LIFE-2): reconsent_checker.bundle.js is included on every
  // page via base.html AND again inside organizer_app.html's script block, so
  // this IIFE can evaluate twice on organizer pages. Defer scripts run
  // sequentially and to completion, so by the time the second copy evaluates the
  // first has already published window.CitaReconsentChecker — bail out so init()
  // (and its window/document lifecycle listeners + 5-min poll interval) is wired
  // EXACTLY ONCE. Without this the pageshow/visibilitychange listeners would
  // double-register and leak for the SPA document's lifetime.
  if (window.CitaReconsentChecker) return;

  // Safe i18n shim: window.__ may not be available yet at IIFE eval time
  // (non-module script). Resolve LAZILY at call time so strings emitted later
  // (e.g. the session-expired toast) still translate once Frappe's client
  // translation fn has loaded — capturing it once would freeze the identity
  // fallback for the whole page lifetime (RC-I18N-SHIM-DEGRADE). Forwards ALL
  // args so Frappe's __(text, replacements, context) interpolation is preserved.
  const __ = (...args) =>
    typeof window.__ === "function" ? window.__(...args) : args[0];

  const RECONSENT_BANNER_ID = "cita-reconsent-banner";
  const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Re-check every 5 minutes
  // v2 schema (Stage-876 F1): version-aware suppression. We no longer store a
  // binary "checked this session" flag (that masked NEWER consent versions —
  // the user accepted vX, the server later bumped to vX+1, and the old flag
  // suppressed the re-prompt). Instead we persist a per-type SIGNATURE map
  // {consent_type: current_version} of what the user has actually seen/cleared.
  // Suppression only fires when EVERY outdated item the server reports is
  // already covered at its current_version. Any version bump invalidates the
  // signature → re-prompt is structurally guaranteed (DAR-F1-D4 sign-off).
  const SESSION_SIG_KEY = "cita.reconsent.checked.v2";
  // Per-session dismiss marker (Welle 2 Window-Mechanik): when the user
  // clicks "Spaeter erinnern" we stop showing the banner *for this tab
  // session only*. A new browser session (tab close + reopen) shows it
  // again — until the server-side window deadline expires.
  const SESSION_DISMISSED_KEY = "cita.reconsent.session_dismissed";
  // v2 schema (Stage-876 F1): the dismiss is now VERSION-AWARE. When the user
  // clicks "Spaeter erinnern" we persist the (type -> current_version)
  // signature of the outdated set that was dismissed — same shape as the
  // checked signature. The skip short-circuit no longer returns before the
  // fetch on the binary flag alone; it suppresses ONLY when the binary flag
  // is set AND the server's current outdated set is still fully covered by
  // the DISMISSED signature. Any version bump (or a never-dismissed type)
  // makes the set un-covered -> the modal re-prompts even after a skip.
  const SESSION_DISMISSED_SIG_KEY = "cita.reconsent.session_dismissed.v2";

  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Whitelist mapping consent_type → public legal-document URL.
  // Items become clickable links opening in a new tab. Unmapped types render
  // as static rows (no anchor) so the modal never produces broken links.
  // Cookies/Marketing fall back to anchor sections inside /datenschutz —
  // there is no separate page today (verified 2026-05-02).
  const consentDocUrls = {
    DSGVO: "/datenschutz",
    AGB: "/agb",
    Marketing: "/datenschutz#marketing",
    Cookies: "/datenschutz#cookies",
    "Partner Inhalte": "/datenschutz#partner",
    organizerterms: "/agb",
    Plattformvereinbarung: "/agb",
    Teilnahmebedingungen: "/agb",
    AVV: "/datenschutz#avv",
    "Vertragliche Anlage": "/datenschutz",
    Impressum: "/impressum",
  };

  // Approximate read-time hint per document, in minutes. Static values —
  // updated when the underlying document materially changes. Anything not
  // listed renders without a read-time hint.
  const consentReadTimes = {
    DSGVO: 5,
    AGB: 6,
    Marketing: 1,
    Cookies: 2,
    "Partner Inhalte": 1,
    organizerterms: 8,
    Plattformvereinbarung: 7,
    Teilnahmebedingungen: 4,
    AVV: 9,
  };

  // Auto-hide guard: when the user is already viewing one of the legal
  // documents, do not pop the modal. Otherwise clicking "DSGVO öffnen" would
  // immediately re-trigger the banner on the destination page and lock the
  // user in a loop. Compares pathname only — fragments are stripped.
  const AUTO_HIDE_PATHS = new Set([
    "/datenschutz",
    "/agb",
    "/impressum",
    "/cookies",
    "/marketing-einwilligung",
  ]);

  const isOnLegalDocPage = () => {
    try {
      const path = (window.location.pathname || "").replace(/\/$/, "");
      return AUTO_HIDE_PATHS.has(path) || AUTO_HIDE_PATHS.has(path + "/");
    } catch {
      return false;
    }
  };

  /** @type {number|null} */
  let checkInterval = null;

  /** @type {boolean} */
  let acceptInFlight = false;

  /** @type {boolean} */
  let skipInFlight = false;

  /**
   * @type {boolean} Session-expiry latch. Once a logged-out (401/403) response
   * has been handled we suppress duplicate toast/redirect from a concurrent
   * skip + accept + poll. Re-armed by the bfcache re-validation hook so a
   * genuinely re-authenticated returning tab can prompt again.
   */
  let sessionExpiredHandled = false;

  /** @type {Element|null} */
  let previouslyFocused = null;

  /**
   * @type {string|null} Flap-guard (F1): signature of the outdated set the
   * banner is currently rendering. When showBanner is called again with the
   * SAME signature while the banner is already visible, we skip the re-render
   * (no focus-steal, no announcer re-fire). Reset in hideBanner.
   */
  let lastShownSignature = null;

  /** @type {Array<{type: string, handler: Function}>} M6 FIX: tracked listeners for cleanup */
  const registeredListeners = [];

  // Welle 2 Window-Mechanik — Skip-state per show. The server tells us
  // (per type) whether the dismiss path is still open; we just track
  // which types to send to skip_reconsent + whether the button should
  // render at all. Window-deadline is the only gate (no per-count cap).
  // ``primaryBlockReason`` carries the reason the skip is blocked when
  // ``dismissibleTypes`` is empty — drives the differentiated mandatory
  // hint. Order: compliance_forbidden > window_expired > no_deadline >
  // dismiss_disabled > "" (none / dismiss is allowed).
  const skipState = {
    enabled: false,
    visibleTypes: [],          // consent_types in the current banner
    dismissibleTypes: [],      // subset where skip_window.allowed === true
    primaryBlockReason: "",
    // v2 (F1): the full (type, current_version) set currently rendered. Used
    // by markSessionDismissed to persist a VERSION-AWARE dismiss signature so
    // a later version bump re-prompts even after the user clicked "skip".
    currentOutdated: [],
  };

  /**
   * Escape HTML special characters to prevent XSS
   * @param {string} str
   * @returns {string}
   */
  const escapeHtml = (str) => {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  /**
   * Check if user is logged in (not Guest)
   * @returns {boolean}
   */
  const isLoggedIn = () => {
    const readUser = window.CitaHttp?.readCitaUser;
    if (typeof readUser === "function") {
      const user = readUser();
      return !!user && user !== "Guest";
    }
    // Fallback to frappe.session if CitaHttp not yet loaded
    return (
      typeof frappe !== "undefined" &&
      frappe.session?.user &&
      frappe.session.user !== "Guest"
    );
  };

  /**
   * Deterministic, order-independent signature for an outdated set. Used by
   * the flap-guard to detect whether a show() call carries the SAME pending
   * (type, version) tuples as the currently visible banner.
   * @param {Array<{consent_type?: string, current_version?: string}>} outdated
   * @returns {string}
   */
  const signatureOf = (outdated) => {
    return (outdated || [])
      .filter((it) => it && it.consent_type)
      .map((it) => `${it.consent_type}=${it.current_version ?? ""}`)
      .sort()
      .join("|");
  };

  /**
   * Read the persisted per-type signature map {consent_type: version} of what
   * the user has already seen/cleared this session. Returns {} on miss or any
   * parse error (fail-soft → never accidentally suppresses).
   * @returns {Record<string, string>}
   */
  const readCheckedSignature = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_SIG_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
      return {};
    } catch {
      return {};
    }
  };

  /**
   * Merge the current_version of each outdated item into the persisted
   * signature map. We MERGE (not replace) so previously-cleared types keep
   * their recorded version across multiple checks within a session.
   * @param {Array<{consent_type?: string, current_version?: string}>} outdated
   */
  const writeCheckedSignature = (outdated) => {
    try {
      const sig = readCheckedSignature();
      for (const it of outdated || []) {
        if (it && it.consent_type) {
          sig[it.consent_type] = it.current_version ?? "";
        }
      }
      sessionStorage.setItem(SESSION_SIG_KEY, JSON.stringify(sig));
    } catch {
      // Ignore storage errors (e.g. private browsing)
    }
  };

  /**
   * True ONLY when every outdated item the server reports is already covered
   * by the persisted signature AT ITS CURRENT VERSION. A version bump (or a
   * type the user has never seen) makes this false → re-prompt. An empty
   * outdated set is vacuously covered (nothing pending).
   * @param {Array<{consent_type?: string, current_version?: string}>} outdated
   * @returns {boolean}
   */
  const isOutdatedSetCovered = (outdated) => {
    const sig = readCheckedSignature();
    for (const it of outdated || []) {
      if (!it || !it.consent_type) continue;
      const seen = sig[it.consent_type];
      if (seen === undefined || seen !== (it.current_version ?? "")) {
        return false;
      }
    }
    return true;
  };

  /**
   * Clear the persisted signature map (for testing or after acceptance).
   * After this the next check re-evaluates against server truth from scratch.
   */
  const clearSessionCheck = () => {
    try {
      sessionStorage.removeItem(SESSION_SIG_KEY);
    } catch {
      // Ignore storage errors
    }
  };

  /**
   * Stop the periodic re-check interval
   */
  const stopInterval = () => {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  };

  /**
   * Start the periodic re-check — IDEMPOTENT (never stacks a second interval).
   * Single SSOT for the poll so init() (and any future live-session recovery)
   * share one path; the `if (!checkInterval)` guard also means that, even if the
   * IIFE were somehow evaluated twice, only one interval is ever live.
   */
  const startInterval = () => {
    if (!checkInterval) {
      checkInterval = setInterval(checkConsentStatus, CHECK_INTERVAL_MS);
    }
  };

  /**
   * Notify other tabs that reconsent was accepted
   */
  const BROADCAST_STORAGE_KEY = "cita.reconsent.broadcast";

  const broadcastAccepted = () => {
    try {
      const bc = new BroadcastChannel("cita-reconsent");
      bc.postMessage({ type: "accepted" });
      bc.close();
    } catch {
      // M3 FIX: BroadcastChannel not supported (Safari <15.4) — localStorage fallback
      try {
        localStorage.setItem(BROADCAST_STORAGE_KEY, Date.now().toString());
      } catch {
        // Private browsing — no cross-tab sync possible
      }
    }
  };

  // Skip-409 (2026-06-07): a postpone must also reach sibling tabs. Unlike
  // "accepted" (which clears their signature so they re-evaluate fresh), a
  // "skipped" makes siblings mirror the per-session dismiss — every tab stays
  // suppressed for the session. This stops a sibling tab's lingering banner
  // from firing a concurrent skip_reconsent (the per-user-lock 409 root cause).
  const BROADCAST_SKIP_STORAGE_KEY = "cita.reconsent.broadcast.skip";

  const broadcastSkipped = () => {
    try {
      const bc = new BroadcastChannel("cita-reconsent");
      bc.postMessage({ type: "skipped" });
      bc.close();
    } catch {
      // BroadcastChannel not supported (Safari <15.4) — localStorage fallback
      try {
        localStorage.setItem(BROADCAST_SKIP_STORAGE_KEY, Date.now().toString());
      } catch {
        // Private browsing — no cross-tab sync possible
      }
    }
  };

  /**
   * Listen for acceptance from other tabs
   */
  const listenForBroadcast = () => {
    let hasBroadcastChannel = false;
    try {
      const bc = new BroadcastChannel("cita-reconsent");
      bc.onmessage = (e) => {
        if (e.data?.type === "accepted") {
          hideBanner();
          // v2 (F1): a sibling tab accepting must NOT write/keep a stale
          // signature here — clear it so this tab re-evaluates fresh against
          // server truth on its next poll (prevents stale-signature masking).
          clearSessionCheck();
          stopInterval();
        } else if (e.data?.type === "skipped") {
          // Skip-409 (2026-06-07): a sibling tab postponed — mirror the
          // per-session dismiss so this tab stays suppressed too (skip is
          // client-side suppression; the server only bumps skip_count). Hides
          // the lingering banner that would otherwise fire a concurrent skip.
          markSessionDismissed();
          hideBanner();
          stopInterval();
        }
      };
      hasBroadcastChannel = true;
    } catch {
      // BroadcastChannel not supported
    }

    // M3 FIX: Fallback for Safari <15.4 — listen for localStorage changes from other tabs
    if (!hasBroadcastChannel) {
      window.addEventListener("storage", (e) => {
        if (e.key === BROADCAST_STORAGE_KEY && e.newValue) {
          hideBanner();
          // v2 (F1): clear our signature so this tab re-evaluates fresh.
          clearSessionCheck();
          stopInterval();
        } else if (e.key === BROADCAST_SKIP_STORAGE_KEY && e.newValue) {
          // Skip-409 (2026-06-07): sibling tab postponed (Safari <15.4
          // fallback) — mirror the per-session dismiss like the BroadcastChannel
          // path so this tab stays suppressed and hides its lingering banner.
          markSessionDismissed();
          hideBanner();
          stopInterval();
        }
      });
    }
  };

  /**
   * Trigger shake animation on modal (visual feedback for blocked dismiss)
   * @param {Element} modal
   */
  const shakeModal = (modal) => {
    if (!modal) return;
    modal.classList.remove("cita-shake");
    // Force reflow so re-adding the class restarts the animation
    void modal.offsetWidth;
    modal.classList.add("cita-shake");
  };

  /**
   * Throttled visible feedback when a dismiss attempt is blocked.
   * Shake alone is easy to misread on touch devices; the toast tells sighted
   * users explicitly why nothing happened. Throttled to one call per 4s so
   * rapid Escape mashing doesn't spam the toast container.
   * Screen-reader users still get the announcer message in handleEscape.
   */
  let lastDismissToastAt = 0;
  const DISMISS_TOAST_COOLDOWN_MS = 4000;
  const showDismissBlockedToast = () => {
    const now = Date.now();
    if (now - lastDismissToastAt < DISMISS_TOAST_COOLDOWN_MS) return;
    lastDismissToastAt = now;
    if (typeof window.showToast === "function") {
      window.showToast(__("Diese Zustimmung kann nicht übersprungen werden."), "info");
    }
  };

  /**
   * Render the list of outdated consents in the banner
   * @param {Array<{consent_type: string, current_version: string}>} outdated
   */
  const renderConsentItems = (outdated) => {
    const container = document.querySelector("[data-reconsent-items]");
    if (!container) return;

    const consentLabels = {
      DSGVO: "Datenschutzerklärung",
      AGB: "Allgemeine Geschäftsbedingungen",
      Marketing: "Marketing-Einwilligung",
      Cookies: "Cookie-Präferenzen",
      "Partner Inhalte": "Partner-Inhalte",
      organizerterms: "Veranstalter-Bedingungen",
      Plattformvereinbarung: "Plattform-Vereinbarung",
      Teilnahmebedingungen: "Teilnahmebedingungen",
      AVV: "Auftragsverarbeitungsvertrag",
      "Vertragliche Anlage": "Vertragliche Anlage",
      Impressum: "Impressum",
    };

    const consentIcons = {
      DSGVO: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
      AGB: '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/>',
      Marketing: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
      Cookies: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
    };

    // External-link affordance icon (Lucide "external-link")
    const externalLinkSvg = `
      <svg class="cita-reconsent-item-external" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
        <path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      </svg>
    `;

    container.innerHTML = outdated
      .map((item) => {
        // Use whitelist lookup for labels (safe), escape fallback values
        const label = consentLabels[item.consent_type] || escapeHtml(item.consent_type);
        const currentVersion = escapeHtml(item.current_version) || "—";
        // Icons from whitelist are safe SVG paths
        const icon = consentIcons[item.consent_type] || consentIcons.AGB;
        // Escape consent_type for data-attribute
        const safeType = escapeHtml(item.consent_type);

        // Defensive lookup: avoid prototype-pollution via crafted consent_type
        // values like "__proto__" / "constructor" that would otherwise resolve
        // to inherited Object members.
        const docUrl = Object.hasOwn(consentDocUrls, item.consent_type)
          ? consentDocUrls[item.consent_type]
          : null;
        const readTime = Object.hasOwn(consentReadTimes, item.consent_type)
          ? consentReadTimes[item.consent_type]
          : null;
        const safeUrl = docUrl ? escapeHtml(docUrl) : null;

        const readTimeMarkup = readTime
          ? `<span class="cita-reconsent-item-readtime">${escapeHtml(String(readTime))} ${__("min Lesezeit")}</span>`
          : "";

        const meta = `
          <div class="cita-reconsent-item-meta">
            <span class="cita-reconsent-item-version">${__("Neue Version:")} ${currentVersion}</span>
            ${readTimeMarkup}
          </div>
        `;

        const innerMarkup = `
          <div class="cita-reconsent-item-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              ${icon}
            </svg>
          </div>
          <div class="cita-reconsent-item-content">
            <div class="cita-reconsent-item-title">${label}</div>
            ${meta}
          </div>
        `;

        if (safeUrl) {
          // a11y label clarifies that activation opens a new tab — important
          // for screen-reader users where the visual external-link glyph is
          // not perceivable.
          const ariaLabel = `${label} ${__("öffnen (neuer Tab)")}`;
          return `
            <a class="cita-reconsent-item"
               data-consent-type="${safeType}"
               href="${safeUrl}"
               target="_blank"
               rel="noopener noreferrer"
               aria-label="${escapeHtml(ariaLabel)}">
              ${innerMarkup}
              ${externalLinkSvg}
            </a>
          `;
        }

        // Fallback for unmapped consent types — render as static row, never
        // produce a broken link. Defensive: a new consent_type added to the
        // backend without URL mapping degrades gracefully.
        return `
          <div class="cita-reconsent-item" data-consent-type="${safeType}">
            ${innerMarkup}
          </div>
        `;
      })
      .join("");
  };

  /**
   * Show the re-consent banner
   * @param {Array<{consent_type: string, current_version: string, skip_window?: {allowed: boolean, deadline: string|null, count: number}}>} outdated
   * @param {{dismiss_enabled?: boolean}=} skip
   */
  const showBanner = (outdated, skip) => {
    const banner = document.getElementById(RECONSENT_BANNER_ID);
    if (!banner) {
      console.warn("[ReconsentChecker] Banner element not found");
      return;
    }

    // Flap-guard (F1): if the banner is already visible with the SAME pending
    // (type, version) set, do not re-render — that would steal focus and
    // re-fire the screen-reader announcer on every 5-min poll. A changed set
    // (new version / new type) falls through and re-renders normally.
    const sig = signatureOf(outdated);
    if (!banner.hidden && sig === lastShownSignature) {
      return;
    }
    lastShownSignature = sig;

    // B2 FIX: Re-register listeners if they were cleaned up by hideBanner()
    if (registeredListeners.length === 0) {
      initEventListeners();
    }

    // Track skip-state from server for this show. The server tells us
    // per-type whether the dismiss path is still open (window check +
    // compliance whitelist). We collect the subset for which the user
    // can still click "Spaeter erinnern" + remember the strongest
    // block-reason for the mandatory-hint copy.
    const cfg = skip || {};
    skipState.enabled = !!cfg.dismiss_enabled;
    // F1: record the full (type, current_version) set currently shown so
    // markSessionDismissed can persist a VERSION-AWARE dismiss signature (a
    // later version bump must re-prompt even after "Spaeter erinnern").
    skipState.currentOutdated = (outdated || []).filter((it) => it && it.consent_type);
    skipState.visibleTypes = (outdated || []).map((it) => it && it.consent_type).filter(Boolean);
    skipState.dismissibleTypes = (outdated || [])
      .filter((it) => it && it.consent_type && it.skip_window && it.skip_window.allowed === true)
      .map((it) => it.consent_type);

    // Pick a single reason that represents WHY skip isn't available.
    // compliance_forbidden ranks highest (DSGVO Art. 28 sub-processor
    // changes etc. — user must read), then window_expired (deadline
    // passed), then no_deadline (legacy row), then dismiss_disabled.
    const reasonRank = {
      compliance_forbidden: 4,
      window_expired: 3,
      no_deadline: 2,
      dismiss_disabled: 1,
      allowed: 0,
    };
    let primary = "";
    let primaryScore = -1;
    for (const it of outdated || []) {
      const r = it && it.skip_window && it.skip_window.reason;
      if (!r || r === "allowed") continue;
      const score = reasonRank[r] ?? 0;
      if (score > primaryScore) {
        primaryScore = score;
        primary = r;
      }
    }
    skipState.primaryBlockReason = primary;

    // Store currently focused element for restore
    previouslyFocused = document.activeElement;

    renderConsentItems(outdated);
    // Reset any stale loading state from a previous show/hide cycle. Important
    // when the banner is reopened after a successful accept on a different
    // tab — the local accept button's disabled flag would otherwise persist.
    setAcceptLoading(false);
    updateSkipButtonState();
    banner.hidden = false;
    document.body.style.overflow = "hidden"; // Prevent background scrolling

    // Focus management for accessibility
    const acceptBtn = banner.querySelector("[data-reconsent-accept]");
    if (acceptBtn) {
      acceptBtn.focus();
    }

    // Announce to screen readers via live region
    const announcer = document.getElementById("cita-reconsent-announcer");
    if (announcer) {
      announcer.textContent = "";
      requestAnimationFrame(() => {
        announcer.textContent = __("Datenschutz-Update: Bitte bestätige deine Zustimmung zu den aktualisierten Dokumenten.");
      });
    }
  };

  /**
   * Hide the re-consent banner
   */
  const hideBanner = () => {
    const banner = document.getElementById(RECONSENT_BANNER_ID);
    if (banner) {
      banner.hidden = true;
      document.body.style.overflow = "";
    }

    // Flap-guard reset (F1): next show() always renders, even if the same
    // signature recurs after the banner was hidden (e.g. accept → re-show).
    lastShownSignature = null;

    // M6 FIX: Clean up tracked event listeners to prevent memory leak
    for (const { type, handler } of registeredListeners) {
      document.removeEventListener(type, handler);
    }
    registeredListeners.length = 0;

    // Restore focus to previously focused element
    if (previouslyFocused && typeof previouslyFocused.focus === "function") {
      previouslyFocused.focus();
      previouslyFocused = null;
    }
  };

  /**
   * Per-session dismiss marker (sessionStorage = browser-tab-scoped).
   * When set, the modal is suppressed for the rest of this tab's life
   * but a new tab/window will see it again — exactly the user-decided
   * "Window-Mechanik" semantics: dismiss = "not now", not "snooze".
   */
  const isSessionDismissed = () => {
    try {
      return sessionStorage.getItem(SESSION_DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  };

  /**
   * Read the persisted per-type version map of what the user DISMISSED this
   * session via "Spaeter erinnern". Mirror of readCheckedSignature. {} on miss.
   * @returns {Record<string, string>}
   */
  const readDismissedSignature = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_DISMISSED_SIG_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
      return {};
    } catch {
      return {};
    }
  };

  /**
   * True ONLY when every currently-outdated item is already covered by the
   * DISMISSED signature at its current_version — i.e. the user skipped exactly
   * this (type, version) set and the server has not bumped anything newer. A
   * version bump (or a never-dismissed type) makes this false → re-prompt.
   * This is the F1 fix: a skip must NOT mask a newer mandatory version.
   * @param {Array<{consent_type?: string, current_version?: string}>} outdated
   * @returns {boolean}
   */
  const isDismissCovered = (outdated) => {
    const items = (outdated || []).filter((it) => it && it.consent_type);
    if (items.length === 0) return false;
    const sig = readDismissedSignature();
    for (const it of items) {
      const seen = sig[it.consent_type];
      if (seen === undefined || seen !== (it.current_version ?? "")) {
        return false;
      }
    }
    return true;
  };

  const markSessionDismissed = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISSED_KEY, "true");
      // F1: persist the VERSION-AWARE signature of what was dismissed, so a
      // later version bump re-prompts even within this tab session.
      const sig = {};
      for (const it of skipState.currentOutdated || []) {
        if (it && it.consent_type) {
          sig[it.consent_type] = it.current_version ?? "";
        }
      }
      sessionStorage.setItem(SESSION_DISMISSED_SIG_KEY, JSON.stringify(sig));
    } catch {
      // Private browsing — no big deal, server still has the count.
    }
  };

  const clearSessionDismissed = () => {
    try {
      sessionStorage.removeItem(SESSION_DISMISSED_KEY);
      sessionStorage.removeItem(SESSION_DISMISSED_SIG_KEY);
    } catch {
      // ignore
    }
  };

  /**
   * Toggle the "Spaeter erinnern" button based on per-type window state
   * the server attached to each outdated consent.
   *
   * Window-Mechanik (Welle 2 — User-decision Thomas 2026-05-02):
   * - Dismiss is allowed while ANY visible type still has its
   *   ``skip_window.allowed === true`` (i.e. now < deadline AND
   *   compliance-skippable AND under hard-limit).
   * - Once all visible types have expired their window, the button
   *   disappears and the modal becomes mandatory.
   */
  const updateSkipButtonState = () => {
    const banner = document.getElementById(RECONSENT_BANNER_ID);
    const skipBtn = banner?.querySelector("[data-reconsent-skip]");
    const skipLabel = skipBtn?.querySelector("[data-reconsent-skip-label]");
    const mandatoryHint = banner?.querySelector("[data-reconsent-mandatory-hint]");
    if (!skipBtn) return;

    const dismissible = skipState.dismissibleTypes || [];

    const banner2 = banner;
    const warning = banner2?.querySelector("[data-reconsent-warning]");

    if (skipState.enabled && dismissible.length > 0) {
      skipBtn.hidden = false;
      skipBtn.removeAttribute("aria-hidden");
      if (skipLabel) {
        skipLabel.textContent = __("Später erinnern");
      }
      if (mandatoryHint) {
        mandatoryHint.textContent = __(
          "Du kannst diese Erinnerung verschieben — sie erscheint in einer neuen Sitzung wieder.",
        );
      }
      if (warning) {
        warning.hidden = true;
        warning.textContent = "";
      }
    } else {
      skipBtn.hidden = true;
      skipBtn.setAttribute("aria-hidden", "true");

      // Differentiated copy per Anna-P1-4: tell the user WHY skip is
      // unavailable. compliance_forbidden gets the strictest message
      // (Art. 28 sub-processor / contractual annex change requires
      // active review). window_expired explains the past deadline.
      // The visible warning-block carries the message; the sr-only
      // mandatory hint mirrors it for assistive tech.
      let warnCopy = "";
      let hintCopy = __(
        "Diese Zustimmung ist erforderlich. Das Fenster kann nicht geschlossen werden.",
      );
      switch (skipState.primaryBlockReason) {
        case "compliance_forbidden":
          warnCopy = __(
            "Diese Aenderung erfordert deine aktive Pruefung (DSGVO Art. 28 Abs. 2 — Sub-Prozessor- bzw. Vertragsanlagen-Aenderung). Bitte bestaetige direkt.",
          );
          hintCopy = warnCopy;
          break;
        case "window_expired":
          warnCopy = __(
            "Die Frist zum Verschieben ist abgelaufen. Bitte bestaetige diese Zustimmung jetzt.",
          );
          hintCopy = warnCopy;
          break;
      }
      if (mandatoryHint) mandatoryHint.textContent = hintCopy;
      if (warning) {
        if (warnCopy) {
          warning.textContent = warnCopy;
          warning.hidden = false;
        } else {
          warning.hidden = true;
          warning.textContent = "";
        }
      }
    }
  };

  /**
   * Toggle the loading state of the accept button (spinner + disabled).
   * Defensively no-ops when target elements are not in the DOM (e.g. during
   * early teardown or if the template was customized).
   */
  const setAcceptLoading = (loading) => {
    const banner = document.getElementById(RECONSENT_BANNER_ID);
    const acceptBtn = banner?.querySelector("[data-reconsent-accept]");
    const spinner = acceptBtn?.querySelector("[data-reconsent-spinner]");
    const label = acceptBtn?.querySelector("[data-reconsent-accept-label]");
    const skipBtn = banner?.querySelector("[data-reconsent-skip]");

    if (!acceptBtn) return;

    if (loading) {
      acceptBtn.disabled = true;
      acceptBtn.setAttribute("aria-disabled", "true");
      acceptBtn.setAttribute("data-loading", "true");
      acceptBtn.setAttribute("aria-busy", "true");
      if (spinner) spinner.hidden = false;
      if (label) label.textContent = __("Wird gespeichert...");
      // Disable secondary action during save to prevent dual-submit races.
      if (skipBtn) {
        skipBtn.disabled = true;
        skipBtn.setAttribute("aria-disabled", "true");
      }
    } else {
      acceptBtn.disabled = false;
      acceptBtn.removeAttribute("aria-disabled");
      acceptBtn.removeAttribute("data-loading");
      acceptBtn.removeAttribute("aria-busy");
      if (spinner) spinner.hidden = true;
      if (label) label.textContent = __("Alle akzeptieren");
      if (skipBtn) {
        skipBtn.disabled = false;
        skipBtn.removeAttribute("aria-disabled");
      }
    }
  };

  // Canonical envelope codes that mean the SERVER SESSION is gone. Verified
  // against api/common/schema.py::ERROR_CODE_CANON + the throw-bridge
  // hooks.py::_FRAPPE_EXC_TO_CITA_CODE:
  //   401 -> 'unauthorized'     idle-timeout reauth (AuthenticationError; also
  //                             sets the X-Cita-Session-Expired:1 header)
  //   403 -> 'permission_error' fully-expired cookie resumes as Guest ->
  //                             PermissionError on an allow_guest=False endpoint
  //   403 -> 'forbidden'        unbridged Frappe-403 fallback for the same
  // DELIBERATELY EXCLUDES 'not_allowed' (403 = skip globally disabled),
  // 'rate_limited' (429), 'conflict' (409) and 'not_found' (404) — those are
  // NOT session-expiry and keep their normal handling.
  const SESSION_GONE_CODES = new Set(["unauthorized", "permission_error", "forbidden"]);

  /**
   * True when a citaFetchJson result represents a gone/expired server session.
   * Accepts BOTH shapes used in this file: {payload, status} (handleSkip's
   * postSkip) and {res, payload} (handleAccept / checkConsentStatus). Pure and
   * fail-soft — an unknown shape returns false (never a spurious redirect).
   * @param {{res?: Response, status?: number, payload?: {code?: string}, code?: string}} result
   * @returns {boolean}
   */
  const isSessionExpiredResult = (result) => {
    if (!result) return false;
    const res = result.res || null;
    const status = result.status ?? res?.status;
    const code = result.payload?.code ?? result.code;
    if (status === 401) return true;
    if (typeof code === "string" && SESSION_GONE_CODES.has(code)) return true;
    try {
      if (res?.headers?.get?.("X-Cita-Session-Expired") === "1") return true;
    } catch {
      // headers not accessible (older/test shape) — status/code already decided
    }
    return false;
  };

  /**
   * Gracefully handle an expired server session detected by any of the three
   * API callers (skip / accept / poll). Idempotent via the sessionExpiredHandled
   * latch so a concurrent skip + accept + poll cannot double-toast/redirect.
   *
   * - ALWAYS: release the modal (hideBanner restores body scroll + removes the
   *   focus trap), stop the 5-min poll against a dead session, and clear any
   *   stuck in-flight/loading state so nothing is left spinning behind us.
   * - If the user was actively blocked — a button click (userAction) OR a
   *   visible mandatory modal — show a non-scary "Sitzung abgelaufen" toast and
   *   reload so the server's own auth/consent flow routes the now-logged-out
   *   user (login for protected pages, public re-render otherwise). This
   *   replaces the old cryptic `throw new Error("Bitte anmelden.")` + dead
   *   "retry" toast (symptoms 1a/1b).
   * - If NO banner was visible (a silent background poll just noticed the dead
   *   session): stop quietly — do NOT yank a reading user off their page.
   * @param {{userAction?: boolean}=} opts
   */
  const handleSessionExpired = ({ userAction = false } = {}) => {
    if (sessionExpiredHandled) return;
    sessionExpiredHandled = true;

    const banner = document.getElementById(RECONSENT_BANNER_ID);
    const bannerWasVisible = !!banner && !banner.hidden;

    // Clear stuck UI + release the modal (no-op if nothing was shown).
    acceptInFlight = false;
    skipInFlight = false;
    setAcceptLoading(false);
    hideBanner();

    if (!userAction && !bannerWasVisible) {
      // Silent background expiry with nothing on screen: keep the 5-min poll
      // ALIVE (do NOT stopInterval) so it self-re-arms via checkConsentStatus's
      // live-session branch and resumes the instant the session returns — each
      // dead-session poll is a cheap latch-guarded no-op. On a long-lived SPA tab
      // (organizer hub) there is no implicit reload to restart a stopped
      // interval, so stopping here would silently end consent polling for the
      // page's whole life (LIFE-1). No toast, no reload: never yank a reading
      // user off their page.
      return;
    }

    // The user was actively blocked — a button click or a visible mandatory
    // modal. Stop the poll (the page reloads next anyway) and route them through
    // a reload so the server decides login-vs-public.
    stopInterval();

    if (typeof window.showToast === "function") {
      window.showToast(
        __("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an."),
        "info",
      );
    }
    // Reload so the SERVER's auth/consent flow decides where a now-logged-out
    // user goes: a protected page redirects to its login (with the server's own
    // redirect-to), a public page simply re-renders as guest without the modal.
    // This is exactly the "nach Aktualisieren"-refresh the user already does —
    // now automatic and explained by the toast above — with ZERO client-side
    // open-redirect surface and no hand-built/guessed login URL (the app has
    // more than one login route). The short delay lets the toast register first.
    try {
      window.setTimeout(() => {
        window.location.reload();
      }, 1200);
    } catch {
      // location not available (test env) — the toast already informed the user
    }
  };

  /**
   * Handle skip button click — Window-Mechanik (Welle 2).
   *
   * POSTs the user's "spaeter erinnern" decision for the types whose
   * skip_window is currently open (server-truth, computed in
   * check_consent_status). On success: hide banner + mark THIS browser
   * session as dismissed (sessionStorage). A new tab/window will see
   * the modal again until the server-side window deadline expires.
   */
  const handleSkip = async () => {
    if (skipInFlight || acceptInFlight) return;
    if (!skipState.enabled) return;

    const types = skipState.dismissibleTypes || [];
    if (types.length === 0) return;

    skipInFlight = true;
    const banner = document.getElementById(RECONSENT_BANNER_ID);
    const skipBtn = banner?.querySelector("[data-reconsent-skip]");
    const skipLabel = skipBtn?.querySelector("[data-reconsent-skip-label]");
    const acceptBtn = banner?.querySelector("[data-reconsent-accept]");
    const previousLabel = skipLabel?.textContent || "";

    if (skipBtn) {
      skipBtn.disabled = true;
      skipBtn.setAttribute("aria-disabled", "true");
    }
    if (acceptBtn) {
      // Disabling accept while a skip is in flight prevents accidental
      // double-submit if the user changes their mind mid-request.
      acceptBtn.disabled = true;
    }
    if (skipLabel) {
      skipLabel.textContent = __("Wird verschoben...");
    }

    try {
      // Send as URLSearchParams object — citaFetchJson recognizes this as
      // a non-JSON body (see http/utils.js::isJsonBody) and the browser
      // attaches the correct application/x-www-form-urlencoded Content-
      // Type automatically. We deliberately avoid setting the header via
      // the `headers` option because buildHeaders() blocks Content-Type
      // overrides from callers. Frappe's form_dict parses the CSV value
      // straight into the function parameter (no JSON-array-as-string
      // ambiguity).
      const formBody = new URLSearchParams();
      formBody.append("consent_types", types.join(","));

      const postSkip = async () => {
        const { res: httpResp, payload } = await window.citaFetchJson(
          "/api/method/cita_events.api.core.my_consents.skip_reconsent",
          {
            method: "POST",
            body: formBody,
          },
        );
        // Return res too so isSessionExpiredResult can also honor the
        // X-Cita-Session-Expired header on the skip path (uniform with
        // accept/poll), not only the 401 status / envelope code.
        return { res: httpResp, payload, status: httpResp?.status };
      };

      // A concurrent skip for the same user is the documented retryable case:
      // HTTP 409 with data.reason === "concurrent_skip_lock".
      const isConcurrentLock = (r) =>
        r?.status === 409 || r?.payload?.data?.reason === "concurrent_skip_lock";

      const finalizeDismissed = (toastMsg) => {
        // Window-Mechanik: kein localStorage-Cooldown — wir markieren nur
        // diese Browser-Session als dismissed (sessionStorage). Eine neue
        // Session zeigt das Modal wieder, bis die serverseitige Window-
        // Deadline abgelaufen ist.
        // v2 (F1): skip is suppressed by the per-session dismiss marker
        // (markSessionDismissed) — NOT by a covering signature, which would
        // mask a future version bump.
        markSessionDismissed();
        hideBanner();
        stopInterval();
        // Skip-409 (2026-06-07): tell sibling tabs to also suppress so their
        // lingering banner can't fire a concurrent skip.
        broadcastSkipped();
        if (typeof window.showToast === "function") {
          window.showToast(
            toastMsg || __("Wir erinnern dich in deiner nächsten Sitzung wieder."),
            "success",
          );
        }
      };

      let result = await postSkip();

      // Skip-409 root cause (2026-06-07): with multiple tabs open, each tab's
      // banner can submit skip_reconsent for the same user. The server holds a
      // per-user lock and returns a RETRYABLE 409 (reason=concurrent_skip_lock,
      // "Bitte versuche es kurz erneut") to the loser. Retry once after a short
      // backoff so this tab completes its own skip instead of erroring out.
      if (isConcurrentLock(result)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        result = await postSkip();
      }

      const res = result.payload;

      // Session expired mid-banner (long-open / cached tab): the server returns
      // a clean 401/'unauthorized' (idle reauth) or 403/'permission_error'
      // (cookie gone) envelope. Handle it gracefully instead of falling through
      // to the generic `throw new Error(res.toast)` below — that path surfaced
      // the reported "[ReconsentChecker] Skip failed: Error: Bitte anmelden."
      // plus a "Verschieben fehlgeschlagen, bitte erneut" toast whose retry can
      // never succeed (symptom 1b).
      if (isSessionExpiredResult(result)) {
        handleSessionExpired({ userAction: true });
        return;
      }

      if (res && res.success && res.data) {
        finalizeDismissed(res.toast);
        return;
      }

      // Still locked after the retry → a sibling tab is definitively performing
      // the skip for this user (its request records the server-side skip_count).
      // Mirror the dismissed state locally instead of surfacing a scary error;
      // the server-side reconsent gate re-enforces if the skip window expired.
      if (isConcurrentLock(result)) {
        finalizeDismissed();
        return;
      }

      throw new Error(res?.toast || "Unknown error");
    } catch (error) {
      console.error("[ReconsentChecker] Skip failed:", error);
      if (typeof window.showToast === "function") {
        window.showToast(
          __("Verschieben fehlgeschlagen. Bitte versuche es erneut."),
          "error",
        );
      }
      if (skipBtn) {
        skipBtn.disabled = false;
        skipBtn.removeAttribute("aria-disabled");
      }
      if (acceptBtn) {
        acceptBtn.disabled = false;
      }
      if (skipLabel && previousLabel) {
        skipLabel.textContent = previousLabel;
      }
    } finally {
      skipInFlight = false;
    }
  };

  /**
   * Handle accept button click
   */
  const handleAccept = async () => {
    // Guard against double-click / parallel calls
    if (acceptInFlight) return;
    acceptInFlight = true;

    setAcceptLoading(true);

    try {
      const { payload: res, res: httpResp } = await window.citaFetchJson(
        "/api/method/cita_events.api.core.my_consents.accept_all_reconsents",
        { method: "POST" }
      );

      // Accept is the ONLY action on a mandatory (non-skippable) modal, so an
      // expired session here would otherwise fully trap the user (skip hidden,
      // Escape/backdrop only shake, generic "Fehler beim Speichern" + dead
      // retry). Detect the logged-out envelope and self-dismiss to login.
      if (isSessionExpiredResult({ res: httpResp, payload: res })) {
        handleSessionExpired({ userAction: true });
        return;
      }

      if (res.success) {
        hideBanner();
        clearSessionCheck();
        // Acceptance supersedes any per-session dismiss flag — clear it
        // so the user doesn't see a phantom dismiss state if a residual
        // banner pops up before the polling cycle catches up.
        clearSessionDismissed();
        stopInterval();
        broadcastAccepted();
        // Reset in-flight flag so a subsequent re-show (different tab,
        // different consent batch) starts from a clean state. Without this
        // the banner could pop back up with the accept button stuck disabled.
        acceptInFlight = false;
        setAcceptLoading(false);

        // Show success toast
        if (typeof window.showToast === "function") {
          window.showToast(res.toast || __("Zustimmungen wurden aktualisiert"), "success");
        }
      } else {
        throw new Error(res?.toast || "Unknown error");
      }
    } catch (error) {
      console.error("[ReconsentChecker] Accept failed:", error);
      acceptInFlight = false;

      // Show error toast
      if (typeof window.showToast === "function") {
        window.showToast(__("Fehler beim Speichern. Bitte versuche es erneut."), "error");
      }

      setAcceptLoading(false);
    }
  };

  /**
   * Check consent status via API
   */
  const checkConsentStatus = async () => {
    if (!isLoggedIn()) return;

    // /core/privacy ist Consent-Dashboard und gleichzeitig das Redirect-Ziel
    // des Server-Side Reconsent-Gate (auth_hooks._enforce_mandatory_consent_gate).
    // Frueherer "page handles reconsent inline"-Branch wurde nie implementiert
    // → Modal hier zu unterdruecken fuehrte 2026-04-27 zur Prod-Sackgasse.
    // ?reconsent=1 ist ground-truth vom Gate und ueberschreibt sessionStorage.
    const forceCheck = new URLSearchParams(window.location.search).has("reconsent");

    // Auto-hide on legal-document pages: when the user clicks an item link
    // the modal would otherwise re-render on the destination and trap them
    // in a loop. Skip the check, but only when the gate hasn't forced us in.
    if (!forceCheck && isOnLegalDocPage()) return;

    // NOTE (F1): both the "already checked this session" AND the "Spaeter
    // erinnern" (dismiss) short-circuits used to return here BEFORE the fetch
    // on binary flags. That masked a NEWER mandatory version for the whole tab
    // session (skip/accept AGB vX → server bumps to vX+1 → suppressed until tab
    // close). Suppression is now VERSION-AWARE and decided AFTER the fetch,
    // against the actual outdated set the server reports: the checked-signature
    // (isOutdatedSetCovered) and the dismissed-signature (isDismissCovered)
    // each suppress ONLY when still covered at the current_version. Any version
    // bump re-prompts. ?reconsent=1 (forceCheck) always shows.

    try {
      const { res: httpResp, payload } = await window.citaFetchJson(
        "/api/method/cita_events.api.core.my_consents.check_consent_status",
        { method: "GET" }
      );

      // Session expired since this tab loaded (long-open / bfcache restore): the
      // poll receives a 401/403 logged-out envelope. fetchJson RESOLVES on 4xx
      // (it never throws), so without this branch the code below reads
      // data={} -> needs_reconsent falsy -> else-branch, which would leave an
      // already-visible mandatory modal on screen with scroll locked (symptom
      // 1a). Self-heal: hide the stale modal + stop polling a dead session.
      // No redirect here unless a banner is actually on screen — a silent
      // background poll must not yank a reading user away (handleSessionExpired
      // makes that call).
      if (isSessionExpiredResult({ res: httpResp, payload })) {
        handleSessionExpired();
        return;
      }

      // Session is alive — re-arm the session-expiry latch so a LATER death of
      // this tab's session (e.g. after a silent expiry + cross-tab re-login
      // without a reload) is handled afresh instead of being swallowed.
      sessionExpiredHandled = false;

      const data = payload?.data || {};
      const skipCfg = data.skip || {};

      if (data.needs_reconsent && data.outdated?.length > 0) {
        // Version-aware suppression (F1): only suppress when EVERY outdated
        // item is already covered at its current_version in our signature.
        // A version bump (or never-seen type) is NOT covered → re-prompt.
        // ?reconsent=1 (forceCheck) always shows. Decided here, after the
        // fetch, so we have the real per-type current_version to compare.
        if (!forceCheck && isOutdatedSetCovered(data.outdated)) {
          return;
        }
        // F1: a per-session "Spaeter erinnern" suppresses ONLY when the
        // dismissed signature still covers the current outdated set at its
        // current_version. A version bump is not covered → re-prompt.
        if (!forceCheck && isSessionDismissed() && isDismissCovered(data.outdated)) {
          return;
        }
        showBanner(data.outdated, skipCfg);
      } else {
        // No reconsent needed — record the (empty) outdated set into the
        // signature and clear any stale dismiss flag (server confirmed
        // nothing pending). writeCheckedSignature([]) is a no-op merge.
        writeCheckedSignature(data.outdated || []);
        clearSessionDismissed();
      }
    } catch (err) {
      // H3 FIX: FAIL-CLOSED — if API unreachable, show banner with generic
      // message. Skip is intentionally disabled in fallback because we
      // don't know the per-type window state.
      console.error("[ReconsentChecker] Check failed, showing fallback banner:", err);
      showBanner(
        [
          { consent_type: "DSGVO", current_version: __("Aktuelle Version") },
          { consent_type: "AGB", current_version: __("Aktuelle Version") },
        ],
        { dismiss_enabled: false },
      );
    }
  };

  /**
   * Initialize event listeners
   */
  /**
   * Register a document event listener and track it for cleanup (M6 FIX).
   * @param {string} type
   * @param {Function} handler
   */
  const addTrackedListener = (type, handler) => {
    document.addEventListener(type, handler);
    registeredListeners.push({ type, handler });
  };

  const initEventListeners = () => {
    // Accept button (delegated)
    addTrackedListener("click", (e) => {
      if (e.target.closest("[data-reconsent-accept]")) {
        e.preventDefault();
        handleAccept();
      }
    });

    // Backdrop click — shake modal to indicate it can't be dismissed
    addTrackedListener("click", (e) => {
      if (e.target.closest("[data-reconsent-backdrop]")) {
        const modal = document.querySelector(".cita-reconsent-modal");
        shakeModal(modal);
        showDismissBlockedToast();
      }
    });

    // Skip ("Spaeter erinnern") button — Welle 2.
    addTrackedListener("click", (e) => {
      if (e.target.closest("[data-reconsent-skip]")) {
        e.preventDefault();
        handleSkip();
      }
    });

    // Keyboard accessibility — trap focus in modal
    addTrackedListener("keydown", (e) => {
      const banner = document.getElementById(RECONSENT_BANNER_ID);
      if (!banner || banner.hidden) return;

      // Prevent Escape from closing (mandatory consent) — shake instead
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        shakeModal(banner.querySelector(".cita-reconsent-modal"));
        // M5 FIX: Announce to screen readers that dismissal is not allowed
        const announcer = document.getElementById("cita-reconsent-announcer");
        if (announcer) {
          announcer.textContent = "";
          requestAnimationFrame(() => {
            announcer.textContent = __("Diese Zustimmung ist erforderlich und kann nicht übersprungen werden.");
          });
        }
        // Sighted users get a visible toast — SR-only announcer is invisible
        // and previously left this state ambiguous on touch devices.
        showDismissBlockedToast();
        return;
      }

      // Tab trapping
      if (e.key === "Tab") {
        const focusables = banner.querySelectorAll(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  };

  /**
   * Initialize the re-consent checker
   */
  let initAttempts = 0;
  const MAX_INIT_ATTEMPTS = 50; // 5 seconds max

  const init = () => {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
      return;
    }

    // Wait for citaFetchJson to be available (max 5s)
    if (typeof window.citaFetchJson !== "function") {
      if (++initAttempts < MAX_INIT_ATTEMPTS) {
        setTimeout(init, 100);
      }
      return;
    }

    initEventListeners();
    listenForBroadcast();

    // Tab-resurrection re-validation (symptom 1a — "Seite zu lange offen").
    // A long-open or bfcache-restored tab runs on a render-time login snapshot
    // (meta[name="cita-user"]) that never learns the server session has died.
    // Re-checking on these events forces a fresh server round-trip: a dead
    // session returns 401/403 -> handleSessionExpired self-heals; a live one
    // changes nothing.
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) {
        // bfcache restore froze-and-restored the page (incl. any stale banner +
        // the session latch). Re-arm the latch and re-validate against the live
        // server so a re-authenticated returning tab can prompt again.
        sessionExpiredHandled = false;
        checkConsentStatus();
      }
    });
    let lastVisibilityCheckAt = 0;
    const VISIBILITY_RECHECK_THROTTLE_MS = 30 * 1000;
    document.addEventListener("visibilitychange", () => {
      // Re-validate when a long-backgrounded tab is refocused (covers the exact
      // "page open too long" report without waiting up to 5 min for the next
      // poll). The latch is intentionally NOT reset here: if the session is
      // still dead this is a no-op; if it was restored the success path runs.
      // Throttled so rapid tab-switching can't hammer the endpoint.
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastVisibilityCheckAt < VISIBILITY_RECHECK_THROTTLE_MS) return;
      lastVisibilityCheckAt = now;
      checkConsentStatus();
    });

    // Initial check after a short delay to not block page load
    setTimeout(checkConsentStatus, 500);

    // Periodic re-check (in case user stays on page for long time)
    startInterval();
  };

  // Start initialization
  init();

  // Expose for testing/debugging
  window.CitaReconsentChecker = {
    check: checkConsentStatus,
    show: showBanner,
    hide: hideBanner,
    clearSession: clearSessionCheck,
    // F1 pure helpers — exported so the vitest spec can pin them directly.
    signatureOf,
    isOutdatedSetCovered,
    readCheckedSignature,
    writeCheckedSignature,
    // F1 version-aware dismiss helpers (skip-path) — exported for the spec.
    isDismissCovered,
    readDismissedSignature,
    markSessionDismissed,
    isSessionDismissed,
    clearSessionDismissed,
    // Session-expiry hardening — pure predicate exported so the vitest spec can
    // pin the 401/403-vs-not_allowed/409 classification bench-free.
    isSessionExpiredResult,
    // Test/QA hook: reset the transient session-expiry latch so successive
    // jsdom specs each start un-latched. No production caller — production
    // re-arms via the pageshow[persisted] (bfcache) hook in init().
    _resetSessionExpiryLatch: () => {
      sessionExpiredHandled = false;
    },
    // Test/QA hooks: drive the (idempotent) poll interval so the LIFE-1 spec can
    // assert the silent expiry path does NOT tear it down. No production caller.
    _startPolling: startInterval,
    _stopPolling: stopInterval,
  };
})();
